<template>
    <div class="fees-by-product-claim w-100 display-flex">
        <AonCol class="grid-cols-12 px-0">
            <AonCard
                class="chart-holder"
                title="Upcoming Fees by Product & Claim Scope"
                :desc="computedDescription"
            >
                <template #header-append>
                    <div class="display-flex align-items-center">
                        <p class="bold ml-lg" style="min-width: 64px">Sort by:</p>
                        <ArmoryOptionToggle
                            class="ml-sm"
                            style="min-width: 326px"
                            v-model="toggleValue"
                            :options="[
                                { label: 'Fees', value: 'fee' },
                                { label: 'Momentum', value: 'momentum' },
                                { label: 'Market Appeal', value: 'marketAppeal' },
                            ]"
                            @update:modelValue="handleToggleChange"
                        />
                    </div>
                </template>
                <div class="card-content">
                    <div id="feesByClaimHeatMap"></div>
                </div>
            </AonCard>
            <AonCoverLoading
                :loading="props.data.length === 0 && !props.loading"
                :show-spinner="false"
                :title="`No Fees By Claim`"
                :message="`No fees were found for ${entityStore.entity?.name} between ${portfolioManagerStore.dateRange[0]} and ${portfolioManagerStore.dateRange[1]}`"
            />
        </AonCol>
    </div>
</template>

<script setup>
import { watch, inject, computed, nextTick, ref } from 'vue'
import { useEntityStore, usePortfolioManagerStore } from '@/stores'

import * as am5 from '@amcharts/amcharts5'
import { useHeatMapChart } from '@/composables/charts/heatMapChart'
import { config } from '@/config'

const entityStore = useEntityStore()
const portfolioManagerStore = usePortfolioManagerStore()
const toggleValue = ref('fee')

let root
let heatMapChart

const emit = defineEmits(['item-clicked'])
const props = defineProps({
    data: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    scrollable: {
        type: Boolean,
        default: true,
    },
})

watch(
    () => props.data,
    (newValue) => {
        initializeChart()
    }
)

const computedDescription = computed(() => {
    const baseText = `Breakdown of future fees organized by product class and patent claim scope. Click on a fee amount to view the patents. `

    if (toggleValue.value === 'fee') {
        return (
            baseText +
            'Sorts products in descending order by maintenance fees over the selected timeframe, placing the product with the highest fees at the top. This view helps identify areas of significant investment that may warrant closer scrutiny.'
        )
    }

    if (toggleValue.value === 'momentum') {
        return (
            baseText +
            'Sorts products in ascending order by momentum, placing the product with the lowest momentum at the top. Low-momentum areas may indicate stagnation and could be ripe for pruning.'
        )
    }

    return (
        baseText +
        'Sorts products in ascending order by predicted market appeal, which is an estimate of how a product might be valued or recognized based on valuations of comparable publicly traded companies operating in similar innovation domains. This view highlights products with relatively low market recognition, which may benefit from pruning.'
    )
})

const transformedData = computed(() => {
    if (!props.data || props.data.length === 0) return []

    // First sort the products based on the selected metric
    const sortedProducts = [...props.data].sort((a, b) => {
        if (toggleValue.value === 'momentum') {
            return a.momentum - b.momentum // Ascending order for momentum
        } else if (toggleValue.value === 'marketAppeal') {
            return a.marketAppeal - b.marketAppeal // Ascending order for market appeal
        }
        // Default to original order for fees
        return 0
    })

    const result = []
    const breadthDisplayMap = {
        narrow: 'Narrow',
        average: 'Average',
        broad: 'Broad',
        veryBroad: 'Very Broad',
    }

    // Create the heatmap data using the sorted products
    sortedProducts.forEach((item) => {
        const product = item.product
        const feesByClaimBreadth = item.feesByClaimBreadth

        Object.keys(feesByClaimBreadth).forEach((breadthKey) => {
            if (breadthKey !== 'notScored') {
                result.push({
                    product: product,
                    breadth: breadthDisplayMap[breadthKey],
                    value: feesByClaimBreadth[breadthKey],
                    dateRange: {
                        from: portfolioManagerStore.dateRange[0],
                        to: portfolioManagerStore.dateRange[1],
                    },
                })
            }
        })
    })

    return result
})

const initializeChart = async () => {
    if (root) {
        root.dispose()
    }
    await nextTick()

    am5.addLicense(config.license.AMChartsLicense)
    root = am5.Root.new('feesByClaimHeatMap')

    heatMapChart = useHeatMapChart(root)

    heatMapChart.setClickCallback(handleCellClick)

    if (transformedData.value.length === 0) return

    heatMapChart.configureHeatmap({ scrollable: props.scrollable })
    heatMapChart.generateAxes(transformedData.value, { scrollable: props.scrollable })
    heatMapChart.generateSeries(transformedData.value, { scrollable: props.scrollable })
}

const handleCellClick = (cellData) => {
    if (cellData && cellData.value !== 0) {
        emit('item-clicked', {
            product: cellData.product,
            breadth: cellData.breadth,
            value: cellData.value,
            dateRange: cellData.dateRange,
            publicationPk: cellData.publicationPk || cellData.value, // Use value as fallback if publicationPk not available
        })
    }
}

const handleToggleChange = (value) => {
    toggleValue.value = value
    initializeChart()
}
</script>

<style lang="scss" scoped>
.fees-by-product-claim {
    width: 100%;
    position: relative;

    #feesByClaimHeatMap {
        height: 100%;
        height: 600px;

        div {
            height: 100%;
        }
    }
}
</style>
